/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from 'react-redux';
import type { RootState } from '@/store';
import { useAppDispatch, useAppSelector } from 'store/hooks.ts';
import {
  buildStartupDiagnostics,
  StartupContext,
  StartupStepError,
} from '@/utilities/startupDiagnostics.ts';
import { selectAuthToken, selectAuthTokenVerification } from '@/store/authToken/authToken.selector';
import { useWebSocket } from '@/hooks/useWebSocket.ts';
import { WSRoomNames } from '@/common/enum';
import { updatePouchDBItem, ScriptsLoader } from '@/utilities';

import { fetchAllGroups, fetchUserDMASettings } from 'store/accountsInfo/accountsInfo.thunk.ts';
import {
  fetchIncidentForParcelSync,
  fetchJPDAIncidentSetting,
  fetchUsersSavedIncidents,
} from 'store/incidents/incidents.thunk.ts';
import {
  fetchApplicants,
  fetchSelectedApplicantSettingsPWA,
} from 'store/applicants/applicants.thunk.ts';
import { Loading } from 'components/primitive';
import { setIsInitApiLoading, setWSConnection } from 'store/connection/connection.actions.ts';
import {
  selectConnectionHealth,
  selectIsInitApiLoading,
  selectWsConnection,
} from 'store/connection/connection.selector.ts';
import { parcelSyncIncidentIDSelector } from 'store/incidents/incidents.selector.ts';
import { fetchAllTeams } from 'store/teams/teams.thunk.ts';
import {
  accountGroupIDSelector,
  selectedGroupNameForParcels,
} from 'store/accountsInfo/accountsInfo.selector.ts';
import { fetchMapLayerID } from 'store/map/map.thunk.ts';
import { setLoadingState, setNonParcelMode } from 'store/accountsInfo/accountsInfo.actions.ts';

// Eight stages in the start-up chain plus teams.
const STARTUP_STEP_COUNT = 9;

// How long a mount may sit without the start-up chain beginning before it is
// reported. The session is already in the store at mount, so this is generous.
const NO_SESSION_TIMEOUT_MS = 10000;

const NO_DATA_GATHERING_INCIDENT_MESSAGE =
  'No data gathering incident is set for the selected county. Set one in the main Damage Assessment program, then press RELOAD.';

/** Runs one named stage, tagging any failure with where in the chain it happened. */
async function runStep<T>(position: number, step: string, run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (cause) {
    throw new StartupStepError({ step, position, total: STARTUP_STEP_COUNT, cause });
  }
}

/**
 * Startup sequence. Each stage starts at most once per mount, and the map is not
 * shown until every stage the account needs has settled.
 *
 *   mount ──▶ [1] startup chain: settings ──▶ parcel-sync incident ──▶ saved
 *             │   incidents ──▶ palmetto groups ──▶ JPDA ──▶ applicant
 *             │   settings ──▶ applicants ──▶ map layer
 *             │
 *             └──▶ dataFetched ──┬──▶ [2] websocket connect + join rooms
 *                                └──▶ [3] teams ──▶ navigate('/map')
 *
 * `connection.isInitApiLoading` is a status output, never an input. These effects
 * write it, so listing it as a dependency made them re-trigger themselves: the
 * whole chain ran a second time the instant the first run cleared the flag, and
 * that second run raced the already-mounted map -- re-nulling the parcel-sync
 * incident and the parcel county name underneath it. Keep it out of the arrays
 * below, and keep each stage behind its own start latch.
 */
const LoadingContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const reduxStore = useStore<RootState>();
  const auth = useAppSelector(selectAuthTokenVerification);
  const token = useAppSelector(selectAuthToken);
  const isApiLoading = useAppSelector(selectIsInitApiLoading);
  const health = useAppSelector(selectConnectionHealth);
  const wsConnection = useAppSelector(selectWsConnection);
  const selectedIncidentForParcelSync = useAppSelector(parcelSyncIncidentIDSelector);
  const selectedGroupID = useAppSelector(accountGroupIDSelector);
  const groupNameForParcels = useAppSelector(selectedGroupNameForParcels);
  const [dataFetched, setDataFetched] = useState(false);
  const { connect, joinRoom } = useWebSocket();
  ScriptsLoader();

  // One latch per stage. Without them the stages restart whenever an unrelated
  // dependency changes mid-flight, which is what produced duplicate API chains
  // and a second WebSocket on every login.
  const startupChainStartedRef = useRef(false);
  const wsConnectStartedRef = useRef(false);
  const exitStartedRef = useRef(false);

  // Mirrored so the latched effects read the current health instead of the value
  // captured when they last ran -- `health` stays out of their dependency arrays
  // for the same reason `isApiLoading` does.
  const healthRef = useRef(health);
  healthRef.current = health;

  // Read at failure time, not captured at render: the point is to report what the
  // chain was actually working with when it broke.
  const readStartupContext = (): StartupContext => {
    const state = reduxStore.getState();
    const { account, selectedGroup, selectedGroupID } = state.accountsInfo;
    return {
      accountId: account?.id ?? auth?.id,
      activeGroup: selectedGroup ? selectedGroup : selectedGroupID,
      userGroup: selectedGroupID,
      dataGatheringIncident: state.incidents.selectedIncidentForParcelSyncID,
      appOnline: state.connection.health,
      idTokenPresent: Boolean(state.authToken.jwtToken),
      sessionExpiresAt: state.authToken.tokenExpiresAt,
    };
  };

  // Single exit to the error screen. Every start-up failure used to be reduced to
  // one fixed sentence with the cause discarded, so a field report could not tell
  // a dropped connection from a missing setting. The cause now travels with it.
  const failStartup = (error: unknown) => {
    console.error('Error occurred while fetching data', error);
    exitStartedRef.current = true;
    dispatch(setIsInitApiLoading(false));
    navigate('/info', {
      state: {
        message:
          (error instanceof StartupStepError && error.headline) ||
          'Something went wrong while loading your data. Please try again.',
        details: buildStartupDiagnostics(error, readStartupContext()),
        buttonText: 'RELOAD',
        action: 'reload',
      },
    });
  };

  type CallbackFunction = () => Promise<string>;
  const fetchDataAndHandleErrors = async (
    callbackFn: CallbackFunction,
    signal?: AbortSignal
  ): Promise<boolean> => {
    try {
      if (healthRef.current) {
        dispatch(setIsInitApiLoading(true));
        await callbackFn();
        dispatch(setIsInitApiLoading(false));
        return true;
      } else {
        dispatch(setIsInitApiLoading(false));
        goToMap();
        return false;
      }
    } catch (error) {
      // An aborted chain was cancelled by this component's own cleanup: either the
      // user has already left, or the session changed and a fresh chain owns the
      // screen now. Its rejection is not a failure -- reporting it sent the user to
      // the error page over a start-up that was still succeeding, and clearing the
      // loading flag here would blank the screen under the chain that replaced it.
      if (signal?.aborted) return false;
      // Return false so callers gate their success-only follow-up (setDataFetched/navigate)
      // behind this redirect instead of advancing past a failed load.
      failStartup(error);
      return false;
    }
  };

  // Single exit to the map. react-router's navigate stays callable after unmount,
  // so a late caller would otherwise be able to move the user off a screen they
  // already reached.
  const goToMap = () => {
    if (exitStartedRef.current) return;
    exitStartedRef.current = true;
    dispatch(setIsInitApiLoading(false));
    navigate('/map');
  };

  // [1] Startup chain. Runs once per mount; the map waits on `dataFetched`.
  useEffect(() => {
    if (auth === null || !token || startupChainStartedRef.current) return;
    startupChainStartedRef.current = true;
    const controller = new AbortController();
    const fetchData = async () => {
      // Same order and same requests as before; each stage is now named so a
      // failure says where it happened instead of only that it happened.
      await runStep(1, 'user settings', () => dispatch(fetchUserDMASettings(controller)).unwrap());
      await runStep(2, 'data gathering incident', () =>
        dispatch(fetchIncidentForParcelSync(controller)).unwrap()
      );

      // Everything below is keyed on this incident, and stage 6 rejects outright
      // without one ('No Incident ID Found'). That rejection used to surface as the
      // generic error, which made the dedicated message in stage [3] unreachable:
      // [3] only runs after this whole chain succeeds, and it cannot succeed
      // without an incident. Check it here, where it is first knowable. The county
      // group in the details matters -- the setting is stored per county, so an
      // incident saved under a different county reads as "none" here.
      if (!reduxStore.getState().incidents.selectedIncidentForParcelSyncID) {
        throw new StartupStepError({
          step: 'data gathering incident',
          position: 2,
          total: STARTUP_STEP_COUNT,
          cause: 'No data gathering incident is set for the selected county group',
          headline: NO_DATA_GATHERING_INCIDENT_MESSAGE,
        });
      }

      await runStep(3, 'saved incidents', () => dispatch(fetchUsersSavedIncidents()).unwrap());
      await runStep(4, 'county groups', () => dispatch(fetchAllGroups(controller)).unwrap());
      await runStep(5, 'JPDA setting', () =>
        dispatch(fetchJPDAIncidentSetting(controller)).unwrap()
      );
      await runStep(6, 'applicant settings', () =>
        dispatch(fetchSelectedApplicantSettingsPWA()).unwrap()
      );
      await runStep(7, 'applicants', () => dispatch(fetchApplicants()).unwrap());
      await runStep(8, 'map layer', () => dispatch(fetchMapLayerID()));

      return 'DONE';
    };

    fetchDataAndHandleErrors(fetchData, controller.signal).then((success) => {
      if (success) setDataFetched(true);
    });

    return () => {
      // Cancel anything still in flight, so a chain the user navigated away from
      // stops writing into the store behind the screen they landed on.
      controller.abort();
      // The aborted chain no longer clears this itself (see fetchDataAndHandleErrors).
      // On a restart the next chain raises it again immediately; on unmount nothing
      // else would, and the flag is global.
      dispatch(setIsInitApiLoading(false));
      // Release the latch too. `isApiLoading` is gone from the dependency array, so
      // the effect can no longer retrigger itself; if the identity of the session
      // really does change the chain should restart rather than stay latched shut
      // against a screen that renders nothing.
      startupChainStartedRef.current = false;
      dispatch(setLoadingState(false));
    };
  }, [token, auth]);

  // [2] WebSocket. Starts once the chain has settled, and runs alongside [3]
  // rather than gating the map on a socket that may never open.
  useEffect(() => {
    if (auth === null || !token || !selectedIncidentForParcelSync || !dataFetched) return;
    if (wsConnection || wsConnectStartedRef.current) return;
    wsConnectStartedRef.current = true;

    type ConnectWsParams = {
      userId: number;
      groupId: number;
      isSuperAdmin: boolean;
      accessToken: string;
    };

    const connectWs = async ({ userId, groupId, isSuperAdmin, accessToken }: ConnectWsParams) => {
      await connect(userId, groupId, isSuperAdmin, accessToken);
    };
    connectWs({
      userId: auth.id,
      groupId: auth.pvActiveGroupID,
      isSuperAdmin: false,
      accessToken: auth.accessToken || '',
    })
      .then(() => {
        dispatch(setWSConnection(true));

        joinRoom(
          WSRoomNames.DMA_APP_IA_ROOM_NAME_BASE + selectedIncidentForParcelSync,
          WSRoomNames.DMA_APP_PARENT_ROOM_NAME_IA,
          updatePouchDBItem
        );
        joinRoom(
          WSRoomNames.DMA_APP_PA_ROOM_NAME_BASE + selectedIncidentForParcelSync,
          WSRoomNames.DMA_APP_PARENT_ROOM_NAME_PA,
          updatePouchDBItem
        );
      })
      .catch((error) => {
        // A socket failure degrades to polling-free offline behaviour; it must not
        // strand the user on the loading screen, so it is logged and not rethrown.
        wsConnectStartedRef.current = false;
        console.error('Error occurred while opening the realtime connection', error);
      });
  }, [token, auth, wsConnection, selectedIncidentForParcelSync, dataFetched]);

  // [3] Teams, then the single exit to the map. Gated on `dataFetched`, so every
  // request in [1] has already resolved by the time this branch is chosen.
  useEffect(() => {
    if (!dataFetched || exitStartedRef.current) return;

    // Stage [1] now reports a missing incident itself; this stays as a guard in case
    // the incident is cleared between the chain settling and this effect running.
    if (!selectedIncidentForParcelSync) {
      failStartup(
        new StartupStepError({
          step: 'data gathering incident',
          position: 2,
          total: STARTUP_STEP_COUNT,
          cause: 'No data gathering incident is set for the selected county group',
          headline: NO_DATA_GATHERING_INCIDENT_MESSAGE,
        })
      );
      return;
    }

    if (groupNameForParcels) {
      dispatch(setNonParcelMode(groupNameForParcels));
    }

    // No active group means fetchAllTeams has no filter to run against and would
    // reject. Go straight to the map, which reports the missing group itself,
    // instead of stalling on the loading screen.
    if (!selectedGroupID) {
      goToMap();
      return;
    }

    const fetchAllTeamsAndGroup = async () => {
      // Awaited: the map reads teams on mount, so navigating while this is still
      // in flight is what left the team picker empty on a cold start.
      await runStep(9, 'teams', () => dispatch(fetchAllTeams()).unwrap());
      return 'DONE';
    };

    fetchDataAndHandleErrors(fetchAllTeamsAndGroup).then((success) => {
      if (success) goToMap();
    });
  }, [selectedIncidentForParcelSync, selectedGroupID, dataFetched]);

  // This component renders nothing while it is not loading, and stage [1] never
  // starts without a session -- so arriving here with no session is a permanently
  // blank page with nothing to report. Turn that into a reportable screen.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (startupChainStartedRef.current || exitStartedRef.current) return;
      const { tokenVerification, token: storedToken } = reduxStore.getState().authToken;
      failStartup(
        new StartupStepError({
          step: 'session',
          position: 0,
          total: STARTUP_STEP_COUNT,
          cause:
            `Start-up never began: account ${tokenVerification ? 'present' : 'missing'}, ` +
            `token ${storedToken ? 'present' : 'missing'}`,
        })
      );
    }, NO_SESSION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  if (isApiLoading) {
    return <Loading message={'App is loading, please wait...'} />;
  } else {
    return null;
  }
};

export default LoadingContainer;
